import { Link } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { SEOPageLayout, InlineCTA, FAQList } from "../../components/seo/SEOPageLayout";

const FAQS = [
  { q: "Where is the safest place to sell diabetic test strips?", a: "The safest option is an in-person transaction with a local buyer at a public location. You can inspect the buyer, review the offer, and complete the transaction on the spot instead of shipping supplies to a company you cannot verify." },
  { q: "Do you have a physical storefront?", a: "No. To keep our overhead low and make it easier for sellers, we operate as a mobile local buyer. We meet at public places in Jacksonville and nearby cities — no need to travel to a fixed address." },
  { q: "Can I sell my test strips at a pharmacy?", a: "Most pharmacies do not buy back diabetic supplies from consumers, even if the boxes are unopened. A dedicated local buyer or a reputable online buyback service is usually the practical option." },
];

export default function WhereToSell() {
  return (
    <SEOPageLayout
      title="Where to Sell Diabetic Test Strips | Cash for Eligible Supplies"
      description="Wondering where to sell diabetic test strips? Learn the safest local options in Jacksonville, FL, how eligibility works, and how to get started today."
      canonical="https://cashforteststripsjacksonville.com/where-to-sell-diabetic-test-strips/"
      breadcrumbLabel="Where to Sell Diabetic Test Strips"
      primaryKeyword="Where to Sell Diabetic Test Strips"
      h1="Where to Sell Diabetic Test Strips"
      intro="If you have unused diabetic test strips or supplies at home, the three most common options are: a local in-person buyer, a mail-in buyback service, or a marketplace listing. This page explains the trade-offs and how to sell eligible supplies safely if you are in Jacksonville, Florida."
      faqs={FAQS}
    >
      {({ openForm }) => (
        <>
          <section data-testid="section-options">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Your Options for Selling Test Strips</h2>
            <div className="space-y-4">
              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h3 className="font-semibold text-lg text-[#1F2937] mb-2" style={{ fontFamily: "Manrope" }}>1. Local in-person buyer</h3>
                <p className="text-[#4B5563] leading-relaxed">You meet the buyer face to face at a public location. They review the box in front of you and pay you on the spot. This is the fastest option and requires no shipping. Best for people who want same-day certainty.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h3 className="font-semibold text-lg text-[#1F2937] mb-2" style={{ fontFamily: "Manrope" }}>2. Mail-in buyback service</h3>
                <p className="text-[#4B5563] leading-relaxed">You ship your boxes to a company and wait for them to inspect and pay. Some are reputable, others are not. Turnaround is usually one to three weeks. You are trusting the company to grade your supplies fairly after they are already in their possession.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h3 className="font-semibold text-lg text-[#1F2937] mb-2" style={{ fontFamily: "Manrope" }}>3. Online marketplace</h3>
                <p className="text-[#4B5563] leading-relaxed">Platforms like eBay allow listings for unopened diabetic supplies within certain rules. This route requires photos, listings, fees, shipping, and dealing with buyer disputes. Not practical for most sellers with only a few boxes.</p>
              </div>
            </div>
          </section>

          <section data-testid="section-how">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>How the Local Process Works With Us</h2>
            <ol className="space-y-3 text-lg text-[#4B5563] list-decimal list-inside">
              <li>Text a photo of the front and back of your box to (904) 955-9646.</li>
              <li>We check the brand, expiration date, and packaging condition and let you know if the item is eligible.</li>
              <li>If it qualifies, we schedule a local meetup at a place that works for you.</li>
              <li>We inspect the supplies on the spot and pay same-day cash.</li>
            </ol>
          </section>

          <section data-testid="section-eligibility">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>How Eligibility Is Determined</h2>
            <ul className="space-y-3 text-lg text-[#4B5563]">
              <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#0F5132] mt-1 flex-shrink-0" /><span>The retail box must be sealed and unopened.</span></li>
              <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#0F5132] mt-1 flex-shrink-0" /><span>At least six months must remain until the expiration date.</span></li>
              <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#0F5132] mt-1 flex-shrink-0" /><span>Packaging cannot have water damage, tears, or writing over the barcode.</span></li>
              <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#0F5132] mt-1 flex-shrink-0" /><span>Not marked or associated with Medicaid or Medicare.</span></li>
            </ul>
            <p className="text-lg text-[#4B5563] leading-relaxed mt-4">
              For the specific brands we can review right now, see the{" "}
              <Link to="/accepted-diabetic-supplies/" className="text-[#0F5132] font-semibold underline">accepted diabetic supplies list</Link>.
            </p>
          </section>

          <section data-testid="section-faq">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-6" style={{ fontFamily: "Manrope" }}>Frequently Asked Questions</h2>
            <FAQList faqs={FAQS} />
          </section>

          <InlineCTA
            onGetStarted={openForm}
            headline="Ready to Sell Locally in Jacksonville?"
            sub="Send a photo and we will tell you if your supplies qualify — no drive required to find out."
          />
        </>
      )}
    </SEOPageLayout>
  );
}
